import { useMemo } from 'react';
import * as Designs from '@pkg/entities/designs';
import { DesignEntity } from '@/lib/enums';
import useDerive from './relationships/useDerive';
import useCombined from './use/combined';

const relateAllEntities = (snapshot, relate) => {
  const relationships = {};

  for (const plural in snapshot.entities) {
    const type = DesignEntity.fromPlural(plural);
    Object.values(snapshot.entities[plural]).forEach((entity) => {
      relationships[entity.uuid] = relate(entity, type);
    });
  }

  return relationships;
};

const InitialRelated = Object.freeze({
  current: {},
  main: {},
});

/**
 * @param {Object} me
 * @param {String} current
 * @param {Object} main
 * @param {Object} snapshot
 * @returns {Object[]}
 */
export default function useRelationships(me, current, main, snapshot) {
  const mainSnapshot = main?.latest?.snapshot;
  const combinedSnapshot = useCombined(snapshot, mainSnapshot, current?.scope);

  const relateCurrent = useDerive(me, combinedSnapshot);
  const relateMain = useDerive(me, mainSnapshot);

  // every design the caller can see is one read, so the scenarios they own and
  // the ones shared with them are already here as one list
  const stored = Designs.useStore((state) => state.scenarios);

  // memoise scenarios
  const scenarios = useMemo(() => {
    const list = stored.list;
    const hash = list.map(({ uuid }) => uuid).join(':');
    return { list, hash };
  }, [stored]);

  // memoise relationships
  return useMemo(() => {
    if (!snapshot || !mainSnapshot) {
      return InitialRelated;
    }

    console.time('Access.designs.relationships');
    const related = { ...InitialRelated };

    // determine relationship for entites in snapshot
    related.current = relateAllEntities(snapshot, relateCurrent);

    // if scenario then also relate all entites in main design
    related.main = current?.is_scenario
      ? relateAllEntities(mainSnapshot, relateMain)
      : structuredClone(current);

    // relate all present scenarios
    related.current[main.uuid] = relateCurrent(main, DesignEntity.DESIGN);
    scenarios.list.forEach((scenario) => {
      const result = relateCurrent(scenario, DesignEntity.SCENARIO);
      related.current[scenario.uuid] = result;
    });

    console.timeEnd('Access.designs.relationships');
    return related;
  }, [
    scenarios.hash,
    relateCurrent,
    relateMain,
    mainSnapshot,
    snapshot,
    current,
  ]);
}
